import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Loader2, Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BecomePartnerPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    partnership_type: "",
    message: "",
    company_website: "",
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/partner`, form);
      if (res.data.success) {
        setSuccess(true);
        toast.success("Partner application submitted successfully!");
      } else {
        toast.error(res.data.message || "Submission failed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 bg-[#0a0e1a]" data-testid="partner-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Application Submitted!</h2>
          <p className="text-sm text-slate-400 leading-relaxed">Thank you for your interest in partnering with EasyQuickFund. Our team will be in touch soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16 px-4 bg-partner min-h-screen" data-testid="partner-page">
      <div className="max-w-2xl mx-auto form-container">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/20 bg-amber-500/5 text-amber-400 text-xs font-medium mb-3">
            <Handshake className="w-3 h-3" /> Partner Program
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="partner-heading">Become a Partner</h1>
          <p className="text-base text-slate-400">Join our partner network and earn competitive commissions.</p>
        </div>

        <Card className="border-slate-800 bg-[#111827]/60 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-white">Partner Application</CardTitle>
            <CardDescription className="text-slate-500">Tell us about yourself and how you'd like to partner.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="partner-form">
              <div className="hp-field" aria-hidden="true">
                <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={handleChange} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="company_name" className="text-slate-300">Company Name *</Label>
                  <Input data-testid="partner-company-name" id="company_name" name="company_name" required value={form.company_name} onChange={handleChange} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 focus-visible:ring-amber-500/50" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact_name" className="text-slate-300">Contact Name *</Label>
                  <Input data-testid="partner-contact-name" id="contact_name" name="contact_name" required value={form.contact_name} onChange={handleChange} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 focus-visible:ring-amber-500/50" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="partner_email" className="text-slate-300">Email *</Label>
                  <Input data-testid="partner-email" id="partner_email" name="email" type="email" required value={form.email} onChange={handleChange} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 focus-visible:ring-amber-500/50" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="partner_phone" className="text-slate-300">Phone *</Label>
                  <Input data-testid="partner-phone" id="partner_phone" name="phone" type="tel" required value={form.phone} onChange={handleChange} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 focus-visible:ring-amber-500/50" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-300">Partnership Type</Label>
                <Select onValueChange={(v) => setForm((prev) => ({ ...prev, partnership_type: v }))}>
                  <SelectTrigger data-testid="partner-type" className="bg-slate-800/50 border-slate-700 text-white focus:ring-amber-500/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e293b] border-slate-700">
                    <SelectItem value="iso">ISO / Broker</SelectItem>
                    <SelectItem value="referral">Referral Partner</SelectItem>
                    <SelectItem value="affiliate">Affiliate</SelectItem>
                    <SelectItem value="strategic">Strategic Partner</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-slate-300">Message</Label>
                <Textarea data-testid="partner-message" id="message" name="message" value={form.message} onChange={handleChange} rows={4} className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 focus-visible:ring-amber-500/50" />
              </div>

              <Button type="submit" disabled={loading} data-testid="partner-submit-btn" className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold h-11 cta-gold">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : "Submit Partner Application"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
